import { Divider, Stack } from '@mui/material';
import React, { Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fullSize } from '../../../Constants';
import useBreakpoint from '../../../hooks/useBreakpoint';
import { getProjects } from '../../../redux/user/sliceUserProjects';
import { IAppReduxStore } from '../../../redux/store';
import ProjectList from './ProjectList/ProjectList';
import ProjectListSidebar from './ProjectList/ProjectListSidebar';
import Footer from '../../General/Footer/Footer';
import Header from '../../General/Header/Header';
import Loading from '../../General/Loading/Loading';
import { ProjectSelectionViewControls } from './ProjectSelectionViewControls';

/**
 * Application view that allows a user to browse their project databases and open then in the ProjectView.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectSelectionView' });

    /** Detect screen size. */
    const { isDesktop } = useBreakpoint();

    /** Allow component to change the URL. */
    const navigate = useNavigate();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Access relevant state variables from the redux store. */
    const loading = useSelector((store: IAppReduxStore) => store.userSettings.loading);
    const projectsLoading = useSelector((store: IAppReduxStore) => store.userProjects.loading);
    const projects = useSelector((store: IAppReduxStore) => store.userProjects.data);

    /**
     * Fetches the user's project list when the component first renders.
     */
    useEffect(() => {
        !projects.length && dispatch(getProjects());
        if (projects && projects.length === 1) {
            /** If the user has only 1 project, navigate to this project */
            const project = projects[0];
            const replicaId = project.replicaId;
            navigate(`project/${replicaId}`);
        }
    }, [projects]);

    /** View data is still loading, so show a Loading screen. */
    if (loading) {
        return <Loading message={t('loading')} />;
    }

    /** Content component. */
    const content = (
        <Stack
            direction='column'
            sx={{
                flexGrow: 4,
            }}
        >
            <ProjectSelectionViewControls />

            <Stack
                direction='row'
                sx={{
                    flexGrow: 4,
                    position: 'relative',
                }}
            >
                {projectsLoading ? <Loading message={t('loadingProjects')} /> : <ProjectList />}
            </Stack>
        </Stack>
    );

    return (
        <Stack
            direction='column'
            sx={{
                flexGrow: 4,
                ...fullSize,
            }}
        >
            <Header />
            {isDesktop ? (
                content
            ) : (
                <Stack
                    direction='row'
                    sx={{
                        flexGrow: 4,
                    }}
                >
                    <ProjectListSidebar />

                    <Divider orientation='vertical' flexItem />

                    {content}
                </Stack>
            )}
            <Footer />
        </Stack>
    );
};
